<template>
  <div class="w-full flex flex-col items-center">
    <!-- View Header -->
    <div v-if="!closeTutorialBar" class="flex flex-row w-full rounded-lg items-center bg-neutral-10 py-1 pl-1 pr-3">
      <div class="flex flex-row items-center gap-0.5 mr-4">
        <div class="p-2.5">
          <img src="../../../assets/icons/creative-tests.svg">
        </div>
        <BaseText type="label" size="md" class="text-text-muted">
          Creative Tests
        </BaseText>
      </div>
      <BaseText type="body" size="sm" class="text-text-normal">
        Analyze groups of ads launched together on a single day.
      </BaseText>
      <div class="ml-auto flex flex-row gap-2 items-center">
        <button class="flex flex-row gap-1.5 my-1.5 ml-2 mr-2.5">
          <img src="../../../assets/icons/play-btn.svg">
          <BaseText type="label" size="sm" class="text-text-muted">
            Tutorial
          </BaseText>
        </button>
        <img src="../../../assets/icons/line.svg">
        <button @click="closeTutorialBar = true">
          <img src="../../../assets/icons/x-button.svg">
        </button>
      </div>
    </div>
    <!-- Loading State -->
    <div v-if="loading" class="flex flex-col items-center justify-center h-full pt-10">
      <BaseLoadingLogo :margin="2" />
    </div>
    <div v-else-if="pinnedCreativeTestDaysLoaded && pinnedCreativeTestDaysLoadedFlag" class="flex flex-col items-center w-full fade-in">
      <!-- Pinned Creative Tests -->
      <div class="flex flex-row justify-between pr-3 items-center w-full mt-3 border-t border-border-normal pt-3">
        <div class="flex flex-row items-center">
          <div class="flex flex-row gap-3 px-4 py-1 items-center">
            <img src="../../../assets/icons/unpinned.svg">
            <BaseText type="label" size="sm" class="text-text-muted">
              Pinned Creative Tests
            </BaseText>
            <BaseText v-if="pinnedCreativeTestDays.length === 0" type="body" size="xs" class="text-neutral-500">
              You don't have any creative tests pinned.
            </BaseText>
            <BaseText v-else-if="pinnedCreativeTestDays.length === 1" type="body" size="xs" class="text-text-muted">
              You have {{ pinnedCreativeTestDays.length }} creative test pinned.
            </BaseText>
            <BaseText v-else type="body" size="xs" class="text-text-muted">
              You have {{ pinnedCreativeTestDays.length }} creative tests pinned.
            </BaseText>
          </div>
        </div>
        <transition>
          <button v-if="pinnedCreativeTestDays.length > 0" class="flex flex-row px-2 gap-1.5 rounded-md transition-colors hover:bg-neutral-25"
          @click.stop.prevent="clearPins()">
            <UnpinIcon class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted">
              Clear Pins
            </BaseText>
          </button>
        </transition>
      </div>

      <!-- Creative Test Days (PINNED) -->
      <div class="w-full" :class="{'border-b': pinnedCreativeTestDays.length > 0}">
        <transition-group>
          <CreativeTestDay 
            v-for="(pinnedCreativeTestDay, index) in pinnedCreativeTestDays"
            :key="`pinned-ct-${index}-${pinnedCreativeTestDay.date}`" 
            :class="{'mb-5': index === pinnedCreativeTestDays.length - 1}"
            :creative-test-day="pinnedCreativeTestDay"
            is-pinned
            @openDetailsDrawer="handleOpenAdvertisementDetailsDrawer($event)"
            @handlePin="pinOrUnpinCreativeTestDay(pinnedCreativeTestDay)"
          />
        </transition-group>
      </div>

      <!-- Creative Test Days (UNPINNED) -->
      <div class="w-full">
        <transition-group>
          <CreativeTestDay 
            v-for="(creativeTestDay, index) in creativeTestDays"
            :key="`ct-${index}-${creativeTestDay.date}`"
            :class="{ 'mb-10': index === creativeTestDays.length - 1 }"
            :creative-test-day="creativeTestDay"
            @openDetailsDrawer="handleOpenAdvertisementDetailsDrawer($event)"
            @handlePin="pinOrUnpinCreativeTestDay(creativeTestDay)"
          />
        </transition-group>
        <!-- Add infinite scroll here... -->
        <infinite-loading
          v-if="creativeTestDays.length"
          :identifier="infiniteId"
          @infinite="getMoreCreativeTests"
        >
          <div slot="spinner">
            <BaseLoadingLogo :margin="2" />
          </div>
          <div slot="no-more" />
          <div slot="no-results" />
        </infinite-loading>
      </div>

      <AdvertisementDetailsDrawer
        v-if="showAdvertisementDetailsDrawer && !trialExpired"
        :advertisement="selectedAdvertisement"
        discovery
        @getMoreAds="$emit('getMoreAds')"
        @close="closeDrawer"
        @save="$emit('reload')"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import AdvertisementDetailsDrawer from '../../advertisements/AdvertisementDetailsDrawer.vue'
import InfiniteLoading from 'vue-infinite-loading'
import ForeplayAPI from '@/api/foreplayServer'
import firebase from '@/api/config/FirebaseConfig'

import CreativeTestDay from './CreativeTestDay.vue'

// Icons
import UnpinIcon from '../../globals/Icons/SpyderV2Icons/UnpinIcon.vue'

export default {
  name: 'CreativeTests',
  components: {
    CreativeTestDay,
    AdvertisementDetailsDrawer,
    InfiniteLoading,
    UnpinIcon
  },
  props: {
    noDrawer: {
      type: Boolean,
      default: () => false
    }
  },
  data () {
    return {
      creativeTestDays: [],
      pinnedDates: [],
      pinnedCreativeTestDays: [],
      selectedAdvertisement: {},
      showAdvertisementDetailsDrawer: false,
      closeTutorialBar: false,
      infiniteId: +new Date(),
      nextId: null,
      pinnedCreativeTestDaysLoadedFlag: false,
      unpinning: false,
      loading: true
    }
  },
  async mounted () {
    await this.fetchCreativeTests()
    await this.fetchPinnedCreativeTestDays()
    this.loading = false
  },
  computed: {
    ...mapGetters('AuthModule', [
      'getUserFreeTrialDate',
      'isFreeTier',
      'getTeam',
      'getUser'
    ]),
    trialExpired () {
      // check if a week has passed since trial and if they're free tier
      return (
        this.isFreeTier &&
        Date.now() > this.getUserFreeTrialDate + 6.048e8 &&
        !this.getTeam &&
        !this.discovery
      )
    },
    pinnedCreativeTestDaysLoaded () {
      return this.pinnedCreativeTestDays.length > 0 || this.pinnedCreativeTestDays.length === 0
    }
  },
  methods: {
    ...mapMutations('AdvertisementsModule', ['SET_AD_TO_OPEN']),
    async fetchCreativeTests () {
      try {
        // Here we need to hit the API and get these groups
        const { aggregations: runningDays, nextId } =
          await ForeplayAPI.Brands.getCreativeTests(
            null,
            this.$router.currentRoute.params.id
          )

        this.nextId = nextId

        this.creativeTestDays = runningDays.map((day) => ({
          date: parseInt(day.date.slice(10)),
          liveCount: day.liveCount,
          count: day.count
        }))
      } catch (error) {
        console.error('Error fetching creative tests', error)
      } finally {
        this.infiniteId += 1
      }
    },
    async getMoreCreativeTests ($state) {
      try {
        if (!this.nextId) {
          if (Object.keys($state).length) {
            $state.complete()
          }
          return
        }
        const { aggregations: runningDays, nextId } =
          await ForeplayAPI.Brands.getCreativeTests(
            this.nextId,
            this.$router.currentRoute.params.id
          )
        this.nextId = nextId

        // Create a set of pinned dates for efficient lookup
        const pinnedDatesSet = new Set(this.pinnedCreativeTestDays.map(day => day.date))

        // Filter runningDays to exclude dates that are in pinnedCreativeTestDays
        const filteredRunningDays = runningDays.filter(day => !pinnedDatesSet.has(parseInt(day.date.slice(10))))

        this.creativeTestDays = this.creativeTestDays.concat(
          filteredRunningDays.map((day) => ({
            date: parseInt(day.date.slice(10)),
            liveCount: day.liveCount,
            count: day.count
          }))
        )

        if (!this.nextId) {
          if (Object.keys($state).length) {
            $state.complete()
          }
          return
        }

        $state.loaded()
      } catch (error) {
        console.log('Error fetching more creative tests', error)
      } finally {
        this.infiniteId += 1
      }
    },
    async fetchPinnedCreativeTestDays () {
      try {
        const db = firebase.firestore()
        const user = this.getUser
        const sortByDate = (a, b) => new Date(b.date) - new Date(a.date)
        if (!user) return

        const querySnapshot = await db.collection('pinned-creative-tests')
          .where('userId', '==', user.user_id)
          .where('brandId', '==', this.$router.currentRoute.params.id)
          .get()
        if (!querySnapshot.empty) {
          const doc = querySnapshot.docs[0]
          this.pinnedDates.push(...doc.data().creativeTestDays)
          this.creativeTestDays = this.creativeTestDays.filter(creativeTestDay => !this.pinnedDates.includes(creativeTestDay.date))
        }
        const pinnedFromElastic = await ForeplayAPI.Brands.getPinnedCreativeTests(this.pinnedDates, this.$router.currentRoute.params.id)

        console.log('Pinned from Elastic:', pinnedFromElastic)
        this.pinnedCreativeTestDays = this.pinnedCreativeTestDays.concat(
          pinnedFromElastic.map((day) => ({
            date: parseInt(day.date),
            liveCount: day.liveCount,
            count: day.count
          }))
        )
        this.pinnedCreativeTestDays.sort(sortByDate)
        this.pinnedCreativeTestDaysLoadedFlag = true
      } catch (error) {
        console.log('Error fetching pinned creative test days', error)
      }
    },
    clearPins () {
      this.pinnedCreativeTestDays.forEach((day) => {
        this.pinOrUnpinCreativeTestDay(day)
      })
    },
    pinOrUnpinCreativeTestDay (creativeTestDay) {
      const db = firebase.firestore()
      const user = this.getUser
      const brandId = this.$router.currentRoute.params.id
      if (!user) return

      const docRef = db.collection('pinned-creative-tests')
        .where('userId', '==', user.user_id)
        .where('brandId', '==', brandId)

      const sortByDate = (a, b) => new Date(b.date) - new Date(a.date)

      if (this.pinnedCreativeTestDays.includes(creativeTestDay)) {
        this.pinnedCreativeTestDays = this.pinnedCreativeTestDays.filter(day => day !== creativeTestDay)
        try {
          docRef.get().then((querySnapshot) => {
            if (!querySnapshot.empty) {
              const doc = querySnapshot.docs[0]
              doc.ref.update({
                creativeTestDays: firebase.firestore.FieldValue.arrayRemove(creativeTestDay.date)
              })
            }
          })
        } catch (error) {
          console.log('Error removing pinned creative test day', error)
        }
        const indexToInsert = this.creativeTestDays.findIndex(day => new Date(day.date) > new Date(creativeTestDay.date))
        if (indexToInsert === -1) {
          this.creativeTestDays.push(creativeTestDay)
        } else {
          this.creativeTestDays.splice(indexToInsert, 0, creativeTestDay)
        }
      } else {
        this.creativeTestDays = this.creativeTestDays.filter(day => day !== creativeTestDay)
        const indexToInsert = this.pinnedCreativeTestDays.findIndex(day => new Date(day.date) > new Date(creativeTestDay.date))
        try {
          docRef.get().then((querySnapshot) => {
            if (!querySnapshot.empty) {
              const doc = querySnapshot.docs[0]
              doc.ref.update({
                creativeTestDays: firebase.firestore.FieldValue.arrayUnion(creativeTestDay.date)
              })
            } else {
              db.collection('pinned-creative-tests').add({
                userId: user.user_id,
                brandId: brandId,
                creativeTestDays: [creativeTestDay.date]
              })
            }
          })
        } catch (error) {
          console.log('Error adding pinned creative test day', error)
        }
        if (indexToInsert === -1) {
          this.pinnedCreativeTestDays.push(creativeTestDay)
        } else {
          this.pinnedCreativeTestDays.splice(indexToInsert, 0, creativeTestDay)
        }
      }
      this.creativeTestDays.sort(sortByDate)
      this.pinnedCreativeTestDays.sort(sortByDate)
    },
    handleOpenAdvertisementDetailsDrawer (advertisement) {
      this.selectedAdvertisement = advertisement
      this.showAdvertisementDetailsDrawer = true
    },
    closeDrawer () {
      this.showAdvertisementDetailsDrawer = false
      this.selectedAdvertisement = {}
      this.SET_AD_TO_OPEN({})
    }
  }
}
</script>

<style scoped>
.v-enter-active, .v-leave-active {
  transition: opacity 200ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.fade-in {
  animation: fadeIn 200ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>